import { clamp, formatTime, rgba } from '../core/util';
import { BOSS_BY_ID } from '../data/bosses';
import { COMMANDER_BY_ID } from '../data/commanders';
import { ELEMENT_OF, ELEMENT_STYLE, type ElementId } from '../data/elements';
import { FAMILY_BY_ID } from '../data/families';
import { LANE_LENGTH, lanePoints, laneSample } from '../data/lanes';
import { FIELD_TOP, GRID_HEIGHT, GRID_WIDTH, LAYOUT } from '../data/ruleset';
import { SHOP, SHOP_MAX_LEVEL } from '../data/shop';
import type { Lane, Side } from '../data/types';
import { minionXY, tileCenter, tileRect, type Match, type MinionState, type ProjectileState, type TowerState } from '../sim/match';
import { assets, drawIcon } from './assets';
import { drawMinionMark, drawTowerMark } from './towerMark';
import { drawBossMark } from './elementArt';
import type { Effects } from './effects';

export const LANE_COLORS: [string, string] = ['#ff9f43', '#38d9f5'];
export const LANE_NAMES: [string, string] = ['LEFT', 'RIGHT'];
export const SIDE_COLORS: [string, string] = ['#4ad9c8', '#ff4b6e'];
const ESSENCE_COLOR = '#ffd23a';
const HUD_INK = '#2a3344';
const HUD_MUTED = 'rgba(42, 51, 68, 0.62)';
const ESSENCE_INK = '#a8740c';
const FONT = 'Montserrat, sans-serif';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const HUD_RECTS = {
  summon: { x: 10, y: 874, w: 150, h: 78 } as Rect,
  commander: { x: 380, y: 874, w: 150, h: 78 } as Rect,
  pause: { x: 486, y: 8, w: 44, h: 44 } as Rect,
  shop: [
    { x: 170, y: 874, w: 97, h: 36 },
    { x: 273, y: 874, w: 97, h: 36 },
    { x: 170, y: 916, w: 97, h: 36 },
    { x: 273, y: 916, w: 97, h: 36 },
  ] as Rect[],
};

export interface DragState {
  tile: number;
  x: number;
  y: number;
  hoverTile: number;
  started: boolean;
}

export interface Toast {
  text: string;
  life: number;
  maxLife: number;
  color: string;
}

export interface Banner {
  title: string;
  subtitle: string;
  life: number;
  maxLife: number;
  color: string;
}

export interface InfoCard {
  tile: number;
  life: number;
}

/** Where the lesson points. Tile numbers are on the viewer's own grid. */
export interface Coach {
  arrow: { kind: 'summon' | 'shop' | 'bosses' } | { kind: 'tile'; tile: number } | { kind: 'bond'; a: number; b: number } | null;
  drag: { from: number; to: number } | null;
  tiles: number[];
}

export interface HudState {
  toast: Toast | null;
  banner: Banner | null;
  info: InfoCard | null;
  pressed: string | null;
  highlight: 'summon' | 'tiles' | 'shop' | 'commander' | null;
  lowEssenceFlash: number;
  tutorial: { text: string; step: number; total: number } | null;
  coach: Coach | null;
  inspect: { kind: 'unit' | 'boss'; index: number } | null;
}

export interface DeckChip {
  kind: 'unit' | 'boss';
  index: number;
  rect: Rect;
}

const DECK_CHIPS: DeckChip[] = (() => {
  const chips: DeckChip[] = [];
  const y = 786;
  const h = 48;
  let x = 10;
  for (let i = 0; i < 5; i++) {
    chips.push({ kind: 'unit', index: i, rect: { x, y, w: 54, h } });
    x += 58;
  }
  x += 6;
  for (let i = 0; i < 3; i++) {
    chips.push({ kind: 'boss', index: i, rect: { x, y, w: 46, h } });
    x += 50;
  }
  return chips;
})();

/** Five unit chips, then three boss chips, in the gap under the player's grid. */
export function deckChips(): DeckChip[] {
  return DECK_CHIPS;
}

export const TUTORIAL_RECT: Rect = { x: 16, y: FIELD_TOP + 48, w: LAYOUT.width - 32, h: 86 };
export const TUTORIAL_SKIP_RECT: Rect = { x: LAYOUT.width - 76, y: FIELD_TOP + 54, w: 56, h: 22 };

export class Renderer {
  /** Which side this screen belongs to. Side 1 sees the board flipped so their fortress stays at the bottom. */
  viewSide: Side = 0;
  private bg: HTMLCanvasElement | null = null;
  private time = 0;
  private towerUpright = false;

  constructor(private readonly ctx: CanvasRenderingContext2D) {}

  /**
   * One painted parchment board. Photo grass, stone medallions, and the cobble
   * strip are not sampled: they were louder than the units.
   */
  private buildBackground(): HTMLCanvasElement {
    const c = document.createElement('canvas');
    c.width = LAYOUT.width;
    c.height = LAYOUT.height;
    const g = c.getContext('2d')!;

    g.fillStyle = '#141224';
    g.fillRect(0, 0, c.width, c.height);

    const playTop = LAYOUT.topHud.h;
    const playH = LAYOUT.bottomHud.y - playTop;
    g.fillStyle = '#bec5a2';
    g.fillRect(0, playTop, c.width, playH);

    this.paintMeadow(g);
    this.drawRoads(g);
    this.paintPlots(g);

    // HUD backgrounds
    g.fillStyle = '#fff8ef';
    g.fillRect(0, 0, c.width, LAYOUT.topHud.h);
    g.fillRect(0, LAYOUT.bottomHud.y, c.width, LAYOUT.bottomHud.h);
    g.fillStyle = 'rgba(42, 51, 68, 0.1)';
    g.fillRect(0, LAYOUT.topHud.h - 1, c.width, 1);
    g.fillRect(0, LAYOUT.bottomHud.y, c.width, 1);

    return c;
  }

  /** A soft meadow wash between the grids. It fades into the parchment; it is not a panel. */
  private paintMeadow(g: CanvasRenderingContext2D): void {
    const top = LAYOUT.enemyGrid.y + GRID_HEIGHT;
    const bot = LAYOUT.playerGrid.y;
    g.fillStyle='#aebb8c';g.fillRect(0,top,LAYOUT.width,bot-top);
    const mid=(top+bot)/2,cx=LAYOUT.width/2;
    g.strokeStyle='#899a76';g.lineWidth=2;
    g.beginPath();g.ellipse(cx,mid,63,38,0,0,Math.PI*2);g.stroke();
    g.fillStyle='#bdc6a4';g.beginPath();g.moveTo(cx,mid-27);g.lineTo(cx+40,mid);g.lineTo(cx,mid+27);g.lineTo(cx-40,mid);g.closePath();g.fill();g.stroke();
    for(const y of [top+5,bot-12]) {
      g.fillStyle='#7f8b72';g.fillRect(LAYOUT.playerGrid.x+8,y,GRID_WIDTH-16,8);
      for(let x=LAYOUT.playerGrid.x+8;x<LAYOUT.playerGrid.x+GRID_WIDTH-10;x+=26) {
        g.fillStyle='#d4d3b9';g.fillRect(x,y-2,23,7);g.fillStyle='#eef0d8';g.fillRect(x+1,y-2,21,2);
      }
    }
    for(const side of [-1,1]) for(let i=0;i<4;i++) {
      const x=cx+side*(88+i*18),y=mid+(i%2?17:-17);
      g.fillStyle=i%2?'#869b71':'#98aa7b';g.beginPath();g.ellipse(x,y,8,5,.4*side,0,Math.PI*2);g.fill();
      g.strokeStyle='#748868';g.lineWidth=1.4;g.beginPath();g.moveTo(x,y);g.lineTo(x-3,y-6);g.moveTo(x,y);g.lineTo(x+4,y-4);g.stroke();
    }
  }

  /** Soft slabs. A small tone shift keeps thirty cells from reading as one stamp. */
  private paintPlots(g: CanvasRenderingContext2D): void {
    const tones = ['#e6ddc6', '#ded7c2', '#e9e1cc', '#d9d5bd'];
    for (const side of [0, 1] as Side[]) {
      for (let t = 0; t < 15; t++) {
        const r = tileRect(side, t);
        const pad = 3;
        g.fillStyle = tones[(t * 3 + side) % tones.length];
        this.roundRect(g, r.x + pad, r.y + pad, r.w - pad * 2, r.h - pad * 2, 12);
        g.fill();
        g.strokeStyle = '#9b9b83';
        g.lineWidth = 1.5;
        g.stroke();
      }
    }
  }

  /**
   * Flat flank paths. Lane color stays on the tower chevron, not as a stripe
   * down the whole road.
   */
  private drawRoads(g: CanvasRenderingContext2D): void {
    const w = LAYOUT.lanes.width;
    const trace = (pts: Array<{ x: number; y: number }>): void => {
      g.beginPath();
      g.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
    };
    const paths = [0, 1].map((l) => lanePoints(l as 0 | 1));
    const fills = ['#ccc3a6', '#c1cabc'];
    const curbs = ['#8b876e', '#7f9585'];

    for (let l = 0; l < 2; l++) {
      g.strokeStyle = curbs[l];
      g.lineWidth = w + 4;
      g.lineCap = 'butt';
      trace(paths[l]);
      g.stroke();
      g.strokeStyle = fills[l];
      g.lineWidth = w - 2;
      trace(paths[l]);
      g.stroke();
      const x=paths[l][0].x, y0=LAYOUT.lanes.topY, y1=LAYOUT.lanes.bottomY;
      g.strokeStyle='rgba(70,81,63,.18)';g.lineWidth=1;
      for(let y=y0+12;y<y1;y+=22) {
        g.beginPath();g.moveTo(x-w/2+3,y);g.lineTo(x+w/2-3,y);g.stroke();
        const seam=x+(Math.floor(y/22)%2?4:-4);
        g.beginPath();g.moveTo(seam,y);g.lineTo(seam,Math.min(y+22,y1));g.stroke();
      }
      g.strokeStyle = 'rgba(255, 248, 239, 0.22)';
      g.lineWidth = 2;
      trace(paths[l]);
      g.stroke();
    }
  }

  roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    g.beginPath();
    g.moveTo(x + r, y);
    g.arcTo(x + w, y, x + w, y + h, r);
    g.arcTo(x + w, y + h, x, y + h, r);
    g.arcTo(x, y + h, x, y, r);
    g.arcTo(x, y, x + w, y, r);
    g.closePath();
  }

  /** Cancels the side-1 world flip so sprites and labels stay upright. */
  private upright(x: number, y: number, draw: () => void): void {
    if (this.viewSide === 0) {
      draw();
      return;
    }
    const ctx = this.ctx;
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(1, -1);
    ctx.translate(-x, -y);
    draw();
    ctx.restore();
  }

  // ------------------------------------------------------------------ frame

  render(match: Match, fx: Effects, drag: DragState | null, hud: HudState, dt: number): void {
    this.time += dt;
    const ctx = this.ctx;
    if (!this.bg) this.bg = this.buildBackground();

    ctx.save();
    if (fx.shake > 0) {
      ctx.translate((Math.random() - 0.5) * fx.shake, (Math.random() - 0.5) * fx.shake);
    }
    ctx.save();
    if (this.viewSide === 1) {
      ctx.translate(0, LAYOUT.height);
      ctx.scale(1, -1);
    }
    ctx.drawImage(this.bg, 0, 0);
    this.drawPaintedCells(match);

    this.drawFortressHearts(match);
    this.drawTerritory(match);
    this.drawPools(match);
    this.drawTowers(match, drag, hud);
    fx.drawTracers(ctx);
    this.drawMinions(match);
    this.drawMilestones(match);
    this.drawProjectiles(match);
    this.drawLasers(match);
    fx.drawParticles(ctx);
    fx.drawTexts(ctx, this.viewSide === 1);
    if (drag && drag.started) this.drawDragGhost(match, drag);
    ctx.restore();
    if (this.viewSide === 1) {
      ctx.fillStyle = '#fff8ef';
      ctx.fillRect(0, 0, LAYOUT.width, LAYOUT.topHud.h);
      ctx.fillRect(0, LAYOUT.bottomHud.y, LAYOUT.width, LAYOUT.bottomHud.h);
    }
    ctx.restore();

    this.drawTopHud(match);
    this.drawBottomHud(match, hud);
    this.drawDeckRow(match, hud);
    this.drawInspect(match, hud);
    this.drawOverlays(match, hud);
    if (fx.flash) {
      ctx.fillStyle = rgba(fx.flash.color, clamp(fx.flash.alpha, 0, 1));
      ctx.fillRect(0, 0, LAYOUT.width, LAYOUT.height);
    }
  }

  // ---------------------------------------------------------------- field

  private drawFortressHearts(match: Match): void {
    const ctx = this.ctx;
    const midX = LAYOUT.width / 2;
    for (const side of [0, 1] as Side[]) {
      const p = match.players[side];
      const y = side === 0 ? LAYOUT.playerGrid.y - 36 : LAYOUT.enemyGrid.y + GRID_HEIGHT + 36;
      const total = match.rules.fortressHearts;
      const spacing = 20;
      const x0 = midX - ((total - 1) * spacing) / 2;
      ctx.fillStyle = 'rgba(255, 248, 236, 0.82)';
      this.roundRect(ctx, x0 - 16, y - 12, (total - 1) * spacing + 32, 24, 12);
      ctx.fill();
      for (let i = 0; i < total; i++) {
        const filled = i < p.hearts;
        const hx = x0 + i * spacing;
        this.upright(hx, y, () => drawIcon(ctx, assets.ui.heart, hx, y, 16, filled ? '#ff5f7a' : '#d9d0c2', 1));
      }
      ctx.font = `700 10px ${FONT}`;
      ctx.fillStyle = side === this.viewSide ? '#0f766c' : '#9a3048';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const labelY = side === 0 ? y + 22 : y - 22;
      const label = side === this.viewSide ? 'Your fortress' : 'Enemy fortress';
      this.upright(midX, labelY, () => {
        ctx.fillText(label, midX, labelY);
      });
    }
  }

  /**
   * Colored cells keep the parchment slabs. The lane is a light wash and a rim,
   * so the board stays the same map with a readable color.
   */
  private drawPaintedCells(match: Match): void {
    if (match.board !== 'painted') return;
    const ctx = this.ctx;
    const wash = [
      { fill: 'rgba(226, 176, 88, 0.18)', edge: 'rgba(176, 112, 36, 0.78)' },
      { fill: 'rgba(72, 176, 164, 0.18)', edge: 'rgba(28, 128, 118, 0.78)' },
    ];
    for (const side of [0, 1] as Side[]) {
      for (let i = 0; i < 15; i++) {
        const lane = match.cellLanes[i];
        if (lane === undefined) continue;
        const r = tileRect(side, i);
        const pad = 3;
        const tone = wash[lane];
        ctx.fillStyle = tone.fill;
        this.roundRect(ctx, r.x + pad, r.y + pad, r.w - pad * 2, r.h - pad * 2, 12);
        ctx.fill();
        ctx.strokeStyle = tone.edge;
        ctx.lineWidth = 2.5;
        ctx.stroke();
        const markX = lane === 0 ? r.x + 16 : r.x + r.w - 16;
        ctx.fillStyle = tone.edge;
        ctx.beginPath();
        ctx.arc(markX, r.y + 16, 4.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  private drawPools(match: Match): void {
    const ctx = this.ctx;
    for (const pool of match.pools) {
      const k = 1 - pool.timer / pool.duration;
      ctx.fillStyle = rgba('#63c93c', 0.18 + 0.1 * Math.sin(this.time * 6));
      ctx.beginPath();
      ctx.ellipse(pool.x, pool.y, pool.radius * 0.9, pool.radius * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = rgba('#a8ff86', 0.5 * k);
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }

  private drawTowers(match: Match, drag: DragState | null, hud: HudState): void {
    const ctx = this.ctx;
    for (const side of [0, 1] as Side[]) {
      const p = match.players[side];
      const mergeable = new Set<number>();
      if (drag && drag.started && side === this.viewSide) {
        const src = p.towers[drag.tile];
        if (src) {
          for (const t of p.towers) {
            if (t && t !== src && t.familyId === src.familyId && t.rank === src.rank && t.rank < match.rules.maxRank) mergeable.add(t.tile);
          }
        }
      }
      for (let i = 0; i < p.towers.length; i++) {
        const t = p.towers[i];
        const r = tileRect(side, i);
        if (drag && drag.started && side === this.viewSide) {
          if (i === drag.tile) {
            ctx.fillStyle = 'rgba(0,0,0,0.35)';
            ctx.fillRect(r.x, r.y, r.w, r.h);
            continue;
          }
          if (mergeable.has(i)) {
            const pulse = 0.5 + 0.5 * Math.sin(this.time * 8);
            ctx.strokeStyle = rgba('#7dff8a', 0.6 + 0.4 * pulse);
            ctx.lineWidth = 3;
            ctx.strokeRect(r.x + 1.5, r.y + 1.5, r.w - 3, r.h - 3);
          } else if (!t) {
            ctx.strokeStyle = 'rgba(255,255,255,0.25)';
            ctx.lineWidth = 2;
            ctx.strokeRect(r.x + 1, r.y + 1, r.w - 2, r.h - 2);
          }
          if (i === drag.hoverTile) {
            ctx.fillStyle = mergeable.has(i) ? 'rgba(125,255,138,0.25)' : 'rgba(255,255,255,0.18)';
            ctx.fillRect(r.x, r.y, r.w, r.h);
          }
        }
        if (hud.highlight === 'tiles' && side === this.viewSide && t) {
          const pulse = 0.5 + 0.5 * Math.sin(this.time * 6);
          ctx.strokeStyle = rgba('#ffffff', 0.4 + 0.5 * pulse);
          ctx.lineWidth = 3;
          ctx.strokeRect(r.x - 2, r.y - 2, r.w + 4, r.h + 4);
        }
        if (hud.coach && side === this.viewSide && hud.coach.tiles.includes(i)) {
          const pulse = 0.45 + 0.55 * Math.sin(this.time * 5);
          ctx.strokeStyle = rgba('#3dce7a', 0.45 + 0.55 * pulse);
          ctx.lineWidth = 4;
          ctx.strokeRect(r.x - 3, r.y - 3, r.w + 6, r.h + 6);
        }
        if (t) this.drawTower(t, r, hud.info !== null && side === this.viewSide && hud.info.tile === i);
      }
    }
    this.drawElementLinks(match);
  }

  /** One mark in the gap the two tiles share. Brightness is the bond, not a stack of lines. */
  private drawElementLinks(match: Match): void {
    const ctx = this.ctx;
    for (const link of match.elementLinks) {
      const a = tileRect(link.side, link.tileA);
      const b = tileRect(link.side, link.tileB);
      const horizontal = Math.abs(a.y - b.y) < 2;
      const pad = 10;
      let x1: number;
      let y1: number;
      let x2: number;
      let y2: number;
      if (horizontal) {
        const left = a.x < b.x ? a : b;
        const right = left === a ? b : a;
        const x = (left.x + left.w + right.x) / 2;
        x1 = x;
        y1 = left.y + pad;
        x2 = x;
        y2 = left.y + left.h - pad;
      } else {
        const top = a.y < b.y ? a : b;
        const bottom = top === a ? b : a;
        const y = (top.y + top.h + bottom.y) / 2;
        x1 = top.x + pad;
        y1 = y;
        x2 = top.x + top.w - pad;
        y2 = y;
      }
      ctx.save();
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      if (link.tier === 'bad') {
        ctx.lineWidth = 4;
        ctx.strokeStyle = '#1c1916';
        ctx.stroke();
      } else if (link.tier === 'best') {
        const pulse = 0.85 + 0.15 * Math.sin(this.time * 2.2);
        ctx.lineWidth = 10;
        ctx.strokeStyle = `rgba(214, 176, 106, ${0.35 * pulse})`;
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.lineWidth = 4.5;
        ctx.strokeStyle = `rgba(184, 132, 52, ${pulse})`;
        ctx.stroke();
      } else if (link.tier === 'good') {
        ctx.lineWidth = 3.6;
        ctx.strokeStyle = 'rgba(176, 132, 64, 0.9)';
        ctx.stroke();
      } else {
        ctx.lineWidth = 2.6;
        ctx.strokeStyle = 'rgba(168, 128, 70, 0.55)';
        ctx.stroke();
      }
      ctx.restore();
    }
  }

  /** Tints the stretch of road a side is actually holding, up to its furthest post. */
  private drawTerritory(match: Match): void {
    const ctx = this.ctx;
    const byLane: Array<Array<{ distance: number; owner: Side | null }>> = [[], []];
    for (const post of match.lanePosts()) byLane[post.lane].push(post);
    ctx.save();
    ctx.lineCap = 'butt';
    for (let lane = 0; lane < 2; lane++) {
      const posts = byLane[lane];
      for (const side of [0, 1] as Side[]) {
        const held = posts.filter((p) => p.owner === side);
        if (!held.length) continue;
        const edge = side === 0 ? Math.max(...held.map((p) => p.distance)) : Math.min(...held.map((p) => p.distance));
        const from = side === 0 ? 10 : edge;
        const to = side === 0 ? edge : LANE_LENGTH - 10;
        if (to - from < 8) continue;
        const a = laneSample(lane as 0 | 1, from);
        const b = laneSample(lane as 0 | 1, to);
        const mine = side === this.viewSide;
        ctx.strokeStyle = mine ? 'rgba(20, 158, 142, 0.32)' : 'rgba(196, 92, 108, 0.34)';
        ctx.lineWidth = LAYOUT.lanes.width - 8;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  /** A coin station on each third of the road. The spark stays above the troops. */
  private drawMilestones(match: Match): void {
    const ctx = this.ctx;
    for (const post of match.lanePosts()) {
      const s = laneSample(post.lane, post.distance);
      const mine = post.owner === this.viewSide;
      const held = post.owner !== null;
      const ring = !held ? 'rgba(42, 51, 68, 0.28)' : mine ? '#149e8e' : '#c46b78';
      this.upright(s.x, s.y, () => {
        ctx.fillStyle = 'rgba(80, 58, 28, 0.22)';
        ctx.beginPath();
        ctx.ellipse(s.x, s.y + 4, 10, 4, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = held ? '#f6d56a' : '#e7d7b0';
        ctx.beginPath();
        ctx.arc(s.x, s.y, 10, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = ring;
        ctx.lineWidth = held ? 3 : 1.25;
        ctx.stroke();
        drawIcon(ctx, assets.ui.spark, s.x, s.y, 11, held ? '#fff8df' : '#8d7b62', held ? 1 : 0.8);
      });
    }
  }

  drawTower(t: TowerState, r: Rect, selected: boolean, ghost = false): void {
    const ctx = this.ctx;
    const fam = FAMILY_BY_ID[t.familyId];
    const cx = r.x + r.w / 2;
    const cy = r.y + r.h / 2;
    if (this.viewSide === 1 && !this.towerUpright) {
      this.towerUpright = true;
      this.upright(cx, cy, () => this.drawTower(t, r, selected, ghost));
      this.towerUpright = false;
      return;
    }
    const pulse = t.pulse < 0.35 ? 1 + 0.22 * Math.sin((t.pulse / 0.35) * Math.PI) : 1;

    ctx.save();
    if (ghost) ctx.globalAlpha = 0.85;
    ctx.translate(cx, cy);
    ctx.scale(pulse, pulse);
    ctx.translate(-cx, -cy);

    // Plate follows the element, so the tile frame matches the turret.
    const elementId = ELEMENT_OF[fam.id];
    const elementStyle = elementId ? ELEMENT_STYLE[elementId] : null;
    const plate = elementStyle?.color ?? fam.color;
    const inset = 4;
    ctx.fillStyle = rgba(elementStyle?.color ?? fam.color, 0.28);
    this.roundRect(ctx, r.x + inset, r.y + inset, r.w - inset * 2, r.h - inset * 2, 10);
    ctx.fill();
    ctx.strokeStyle = t.rank >= 5 ? '#c4923a' : 'rgba(42, 51, 68, 0.14)';
    ctx.lineWidth = t.rank >= 5 ? 2 : 1;
    this.roundRect(ctx, r.x + inset, r.y + inset, r.w - inset * 2, r.h - inset * 2, 8);
    ctx.stroke();
    if (selected) {
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.lineWidth = 2;
      this.roundRect(ctx, r.x + 1, r.y + 1, r.w - 2, r.h - 2, 8);
      ctx.stroke();
    }

    if (selected && fam.tower.range > 0) {
      ctx.beginPath();
      ctx.arc(cx, cy, fam.tower.range, 0, Math.PI * 2);
      ctx.strokeStyle = rgba(plate, 0.55);
      ctx.lineWidth = 2;
      ctx.setLineDash([7, 6]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    const laneCol = LANE_COLORS[t.lane];

    const markX = t.lane === 0 ? r.x + r.w - 12 : r.x + 12;
    if (fam.tower.attack === 'support') {
      ctx.fillStyle = rgba(fam.accent, 0.9);
      ctx.beginPath();
      ctx.arc(markX, r.y + 14, 3.5, 0, Math.PI * 2);
      ctx.fill();
    } else if (t.supportBonus > 0) {
      ctx.fillStyle = rgba('#ffe6bd', 0.9);
      ctx.beginPath();
      ctx.arc(markX, r.y + 14, 3.5, 0, Math.PI * 2);
      ctx.fill();
    }

    const recoil = t.sinceShot < 0.16 ? 1 - t.sinceShot / 0.16 : 0;
    const aim = this.viewSide === 1 ? -t.angle : t.angle;
    drawTowerMark(ctx, fam.id, cx, cy + 2, 72, t.rank, aim, recoil, this.time);

    const bx = t.lane === 0 ? r.x + 13 : r.x + r.w - 13;
    const by = r.y + 15;
    ctx.fillStyle = laneCol;
    ctx.beginPath();
    if (t.lane === 0) {
      ctx.moveTo(bx - 7, by);
      ctx.lineTo(bx + 5, by - 5.5);
      ctx.lineTo(bx + 5, by + 5.5);
    } else {
      ctx.moveTo(bx + 7, by);
      ctx.lineTo(bx - 5, by - 5.5);
      ctx.lineTo(bx - 5, by + 5.5);
    }
    ctx.closePath();
    ctx.fill();

    // Rank badge
    const rx = r.x + r.w - 12;
    const ry = r.y + r.h - 12;
    ctx.fillStyle = t.rank >= 5 ? '#ffd23a' : 'rgba(10,8,20,0.9)';
    ctx.beginPath();
    ctx.arc(rx, ry, 9, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = rgba(elementStyle?.hot ?? fam.accent, 0.9);
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.fillStyle = t.rank >= 5 ? '#1a1400' : '#fff';
    ctx.font = `800 11px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(t.rank), rx, ry + 0.5);

    // Spawn progress bar
    const interval = Math.max(0.01, this.spawnIntervalFor(t));
    const k = clamp(1 - t.spawnTimer / interval, 0, 1);
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(r.x + 10, r.y + r.h - 7, r.w - 32, 3);
    ctx.fillStyle = '#c6a15a';
    ctx.fillRect(r.x + 10, r.y + r.h - 7, (r.w - 32) * k, 3);

    ctx.restore();
  }

  private spawnIntervalFor(t: TowerState): number {
    // Mirror of Match.spawnInterval without needing the match reference: approximate via stored timer max.
    return this.lastInterval.get(t.id) ?? t.spawnTimer;
  }
  private lastInterval = new Map<number, number>();
  private minionOrder: MinionState[] = [];
  private sortY = new Map<number, number>();

  /** Called by the game each frame so the spawn bar knows the full interval. */
  noteSpawnIntervals(match: Match): void {
    for (const side of [0, 1] as Side[]) {
      for (const t of match.players[side].towers) {
        if (!t) continue;
        const fam = FAMILY_BY_ID[t.familyId];
        this.lastInterval.set(t.id, match.spawnInterval(side, fam, t.rank, t.supportBonus));
      }
    }
  }

  private drawDragGhost(match: Match, drag: DragState): void {
    const t = match.players[this.viewSide].towers[drag.tile];
    if (!t) return;
    const size = LAYOUT.playerGrid.tile;
    const lift = this.viewSide === 1 ? 10 : -10;
    const r: Rect = { x: drag.x - size / 2, y: drag.y + lift - size / 2, w: size, h: size };
    this.ctx.save();
    this.ctx.globalAlpha = 0.9;
    this.drawTower(t, r, false, true);
    this.ctx.restore();
  }

  /** A colored pool under the feet so the element reads before the portrait does. */
  private drawElementAura(element: ElementId, x: number, y: number, bodyH: number): void {
    const style = ELEMENT_STYLE[element];
    const ctx = this.ctx;
    const glow = rgba(style.color, 0.35);
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.ellipse(x, y + bodyH * 0.26, bodyH * 0.58, bodyH * 0.24, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  /** Embers, droplets, pebbles, or gusts. Drawn over the portrait. */
  private drawElementMotes(element: ElementId, x: number, y: number, bodyH: number, moving: boolean, age: number, id: number): void {
    const style = ELEMENT_STYLE[element];
    const ctx = this.ctx;
    ctx.save();
    const count = element === 'earth' ? 3 : 4;
    for (let i = 0; i < count; i++) {
      const seed = id * 1.7 + i * 2.4;
      if (element === 'fire') {
        const rise = (age * 0.65 + seed) % 1;
        const px = x + Math.sin(seed * 3 + age) * bodyH * 0.2;
        const py = y + bodyH * 0.12 - rise * bodyH * 0.72;
        ctx.globalAlpha = 1 - rise;
        ctx.fillStyle = i % 2 === 0 ? style.hot : style.color;
        ctx.beginPath();
        ctx.arc(px, py, 1.5 + (1 - rise) * 1.4, 0, Math.PI * 2);
        ctx.fill();
      } else if (element === 'water') {
        const a = age * 1.3 + seed;
        const px = x + Math.cos(a) * bodyH * 0.32;
        const py = y - bodyH * 0.08 + Math.sin(a * 2) * bodyH * 0.1;
        ctx.fillStyle = rgba(style.hot, 0.9);
        ctx.beginPath();
        ctx.ellipse(px, py, 2.1, 3.1, a, 0, Math.PI * 2);
        ctx.fill();
        if (i === 0) {
          ctx.fillStyle = rgba(style.color, 0.35);
          ctx.beginPath();
          ctx.ellipse(x, y + bodyH * 0.22, bodyH * (0.22 + Math.sin(age * 2) * 0.04), bodyH * 0.08, 0, 0, Math.PI * 2);
          ctx.fill();
        }
      } else if (element === 'earth') {
        const px = x + Math.cos(seed) * bodyH * 0.32;
        const py = y + bodyH * 0.24 + Math.sin(age * 2 + seed) * 1.1;
        ctx.fillStyle = i % 2 === 0 ? style.deep : style.hot;
        ctx.fillRect(px - 1.6, py - 1.2, 3.4, 2.6);
      } else {
        const travel = (age * (moving ? 2.6 : 0.7) + seed) % 1;
        const px = x - bodyH * 0.1 - travel * bodyH * 0.62;
        const py = y - bodyH * 0.28 + Math.sin(seed) * bodyH * 0.22;
        ctx.strokeStyle = rgba(style.color, moving ? 0.8 : 0.4);
        ctx.lineWidth = moving ? 1.8 : 1.2;
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(px + bodyH * (moving ? 0.22 : 0.12), py - 0.6);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  /** Sword swings, bow draws, and the other tells. Drawn over the still portrait. */
  private drawMinionAction(m: MinionState, x: number, y: number, bodyH: number, facing: number): void {
    if (m.isBoss) return;
    const ctx = this.ctx;
    const since = m.attackInterval - m.attackTimer;
    const fam = FAMILY_BY_ID[m.familyId];
    const ink = ELEMENT_STYLE[ELEMENT_OF[m.familyId]]?.hot ?? fam?.accent ?? '#ffffff';
    const swinging = m.engaged && since >= 0 && since < 0.32 && m.damage > 0;
    const t = swinging ? since / 0.32 : 0;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(facing - Math.PI / 2);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (m.familyId === 'pebble') {
      if (swinging) {
        ctx.strokeStyle='#d7cb91'; ctx.lineWidth=2;
        ctx.beginPath();ctx.arc(0,0,bodyH*.35,-1.8,-1.8+t*4);ctx.stroke();
      }
      ctx.restore();return;
    }
    if (m.kind === 'ranged') {
      const pulling = m.engaged && m.attackTimer > 0 && m.attackTimer < 0.45;
      const released = m.engaged && since >= 0 && since < 0.16;
      if (pulling || released) {
        const pull = pulling ? (0.45 - m.attackTimer) / 0.45 : 0;
        const h = bodyH * 0.5;
        ctx.strokeStyle = '#e7d3a1';
        ctx.lineWidth = Math.max(2, bodyH * 0.055);
        ctx.beginPath();
        ctx.moveTo(bodyH * 0.16, -h * 0.58);
        ctx.quadraticCurveTo(bodyH * 0.42, 0, bodyH * 0.16, h * 0.58);
        ctx.stroke();
        const stringX = bodyH * 0.16 - pull * bodyH * 0.42;
        ctx.strokeStyle = '#f7f4ea';
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(bodyH * 0.16, -h * 0.55);
        ctx.lineTo(stringX, 0);
        ctx.lineTo(bodyH * 0.16, h * 0.55);
        ctx.stroke();
        if (pulling && pull > 0.15) {
          ctx.strokeStyle = ink;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(stringX, 0);
          ctx.lineTo(stringX + bodyH * 0.46, 0);
          ctx.stroke();
          ctx.fillStyle = ink;
          ctx.beginPath();
          ctx.moveTo(stringX + bodyH * 0.5, 0);
          ctx.lineTo(stringX + bodyH * 0.4, -3);
          ctx.lineTo(stringX + bodyH * 0.4, 3);
          ctx.fill();
        } else if (released) {
          ctx.strokeStyle = rgba('#ffffff', 0.85);
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(bodyH * 0.42, 0);
          ctx.lineTo(bodyH * 0.78, 0);
          ctx.stroke();
        }
      }
      ctx.restore();
      return;
    }

    if (m.kind === 'sapper') {
      const pulse = 0.5 + 0.5 * Math.sin(m.age * 14);
      ctx.strokeStyle = rgba('#ffb15a', m.engaged ? 0.85 : 0.35);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(bodyH * 0.12, bodyH * 0.12, bodyH * (0.16 + pulse * 0.08), 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
      return;
    }

    if (m.kind === 'firespitter') {
      const jab = swinging ? Math.sin(t * Math.PI) : 0.15;
      ctx.fillStyle = `rgba(255,90,26,${0.25 + jab * 0.6})`;
      ctx.beginPath();
      ctx.ellipse(bodyH * (0.16 + jab * 0.28), -bodyH * 0.02, bodyH * (0.1 + jab * 0.16), bodyH * 0.09, -0.3, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#ffd23a';
      ctx.beginPath();
      ctx.ellipse(bodyH * (0.22 + jab * 0.32), -bodyH * 0.04, bodyH * 0.05, bodyH * 0.035, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    }

    const healed = m.kind === 'healer' && m.specialTimer > 0 && (m.special.healCooldown ?? 1.4) - m.specialTimer < 0.35;
    if (m.kind === 'healer') {
      ctx.strokeStyle = healed ? '#d8ffb0' : '#7dce55';
      ctx.lineWidth = healed ? 3.5 : 2.5;
      const lift = swinging ? Math.sin(t * Math.PI) * bodyH * 0.12 : 0;
      ctx.beginPath();
      ctx.moveTo(bodyH * 0.08, -bodyH * 0.32 - lift);
      ctx.lineTo(bodyH * 0.08, bodyH * 0.18 - lift * 0.3);
      ctx.moveTo(bodyH * 0.08 - 7, -bodyH * 0.2 - lift);
      ctx.lineTo(bodyH * 0.08 + 7, -bodyH * 0.2 - lift);
      ctx.stroke();
      if (healed) {
        ctx.fillStyle = 'rgba(182,242,122,0.4)';
        ctx.beginPath();
        ctx.arc(0, 0, bodyH * 0.28, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
      return;
    }

    if (!swinging && !m.engaged) {
      const rest = Math.sin(m.age * 2 + m.id) * 0.06;
      ctx.globalAlpha = 0.72;
      ctx.save();
      ctx.rotate(-0.4 + rest);
      ctx.fillStyle = '#f4f1ea';
      ctx.fillRect(bodyH * 0.04, -1.4, bodyH * 0.28, 2.8);
      ctx.restore();
      ctx.restore();
      return;
    }
    const runner = m.familyId === 'skirmisher';
    const pike = m.familyId === 'vanguard';
    const tank = m.kind === 'tank';
    const ease = swinging ? (t < 0.3 ? (t / 0.3) * 0.18 : 0.18 + ((t - 0.3) / 0.7) * 0.82) : 0;
    if (pike) {
      const thrust = swinging ? Math.sin(ease * Math.PI) : 0.08 + Math.sin(m.age * 2) * 0.03;
      ctx.strokeStyle = '#f4f7fb';
      ctx.lineWidth = Math.max(2, bodyH * 0.055);
      ctx.beginPath();
      ctx.moveTo(-bodyH * 0.02, bodyH * 0.08);
      ctx.lineTo(bodyH * (0.2 + thrust * 0.42), -bodyH * (0.12 + thrust * 0.22));
      ctx.stroke();
      ctx.fillStyle = ink;
      const tipX = bodyH * (0.2 + thrust * 0.42);
      const tipY = -bodyH * (0.12 + thrust * 0.22);
      ctx.beginPath();
      ctx.moveTo(tipX + bodyH * 0.08, tipY - bodyH * 0.04);
      ctx.lineTo(tipX - bodyH * 0.02, tipY + bodyH * 0.05);
      ctx.lineTo(tipX + bodyH * 0.02, tipY - bodyH * 0.08);
      ctx.fill();
    } else {
      const sweep = swinging ? -1.25 + ease * 2.45 : -0.32 + Math.sin(m.age * 2) * 0.08;
      const reach = runner ? bodyH * 0.4 : tank ? bodyH * 0.34 : bodyH * 0.52;
      const drawBlade = (ang: number, w: number) => {
        ctx.save();
        ctx.rotate(ang);
        ctx.fillStyle = tank ? '#d7c4a2' : '#f4f1ea';
        ctx.fillRect(bodyH * 0.02, -w / 2, reach, w);
        ctx.fillStyle = ink;
        ctx.beginPath();
        ctx.moveTo(reach, -w * 0.8);
        ctx.lineTo(reach + bodyH * 0.1, 0);
        ctx.lineTo(reach, w * 0.8);
        ctx.fill();
        ctx.restore();
      };
      if (runner) {
        drawBlade(sweep, 2.4);
        drawBlade(sweep - 1.1, 2.2);
      } else {
        drawBlade(sweep, tank ? 5 : 3.2);
      }
      if (swinging) {
        ctx.strokeStyle = rgba('#ffffff', 0.55 * Math.sin(Math.min(1, ease) * Math.PI));
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(0, 0, reach * 0.82, -1.15, sweep);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  private drawMinions(match: Match): void {
    const list = this.minionOrder;
    list.length = 0;
    for (const m of match.minions) list.push(m);
    const yOf = this.sortY;
    yOf.clear();
    for (const m of list) yOf.set(m.id, minionXY(m).y);
    list.sort((a, b) => {
      const dy = (yOf.get(a.id) ?? 0) - (yOf.get(b.id) ?? 0);
      return this.viewSide === 1 ? -dy : dy;
    });
    const lead = [-1, -1, -1, -1];
    for (const m of list) {
      if (m.hp <= 0) continue;
      const key = m.side * 2 + m.lane;
      if (m.pos > lead[key]) lead[key] = m.pos;
    }
    for (const m of list) {
      const key = m.side * 2 + m.lane;
      const foe = lead[(1 - m.side) * 2 + m.lane];
      const atFront = lead[key] >= 0 && lead[key] - m.pos <= 18;
      const fighting = foe >= 0 && LANE_LENGTH - lead[key] - foe < 80;
      this.drawMinion(m, atFront && fighting);
    }
  }

  private drawMinion(m: MinionState, frontMotes: boolean): void {
    const ctx = this.ctx;
    const { x, y } = minionXY(m);
    const s = m.scale;
    const stage = Math.min(5, m.isBoss ? 4 : m.rank);
    let bodyH = (m.isBoss ? 58 : 34 + stage * 2) * s;
    // Vector silhouettes share a square footprint; legacy portrait ratios no longer size them.
    if (!m.isBoss) bodyH = Math.min(bodyH, LAYOUT.lanes.width - 18);
    const along = m.side === 0 ? m.pos : LANE_LENGTH - m.pos;
    const dir = laneSample(m.lane, along);
    const sign = m.side === 0 ? 1 : -1;
    const facing = Math.atan2(dir.ty * sign, dir.tx * sign) + Math.PI / 2;
    const fam = m.isBoss ? null : FAMILY_BY_ID[m.familyId];
    ctx.save();
    if (this.viewSide === 1) {
      ctx.translate(x, y);
      ctx.scale(1, -1);
      ctx.translate(-x, -y);
    }

    // Shadow + side ring
    const foot = m.isBoss ? 0.42 : 0.28;
    ctx.fillStyle = 'rgba(0,0,0,0.22)';
    ctx.beginPath();
    ctx.ellipse(x, y + bodyH * 0.28, bodyH * foot, bodyH * 0.14, 0, 0, Math.PI * 2);
    ctx.fill();
    const ring = m.isBoss ? 0.5 : 0.32;
    ctx.strokeStyle = rgba(m.side === this.viewSide ? '#4ad9c8' : '#ff4b6e', m.isBoss ? 0.9 : 0.32);
    ctx.lineWidth = m.isBoss ? 3 : 1;
    ctx.beginPath();
    ctx.ellipse(x, y + bodyH * 0.3, bodyH * ring, bodyH * 0.2, 0, 0, Math.PI * 2);
    ctx.stroke();
    if (m.isBoss) {
      ctx.strokeStyle = rgba('#ffd23a', 0.35 + 0.25 * Math.sin(this.time * 5));
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.ellipse(x, y + bodyH * 0.3, bodyH * 0.62, bodyH * 0.34, 0, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Bosses keep their painted portraits. Ordinary minions are drawn per family, and grow with merge rank.

    const stepping = m.moving && !m.engaged;
    const phase = m.age * 7 + m.id;
    const stride = stepping ? Math.sin(phase) : 0;
    const sinceShot = m.attackInterval - m.attackTimer;
    const striking = m.engaged && sinceShot >= 0 && sinceShot < 0.32 && m.damage > 0 && m.kind !== 'ranged';
    const pulling = m.kind === 'ranged' && m.engaged && m.attackTimer > 0 && m.attackTimer < 0.45;
    const strike = striking ? Math.sin((sinceShot / 0.32) * Math.PI) : 0;
    const bob = stepping ? -Math.abs(stride) * 3.4 : m.engaged ? Math.sin(m.age * 8 + m.id) * 0.6 : Math.sin(this.time * 1.6 + m.id) * 0.7;
    const squash = stepping ? 1 - Math.abs(stride) * 0.06 : 1;
    const stretch = stepping ? 1 + Math.abs(stride) * 0.05 : 1 + Math.sin(this.time * 1.6 + m.id) * 0.018;
    const drawY = y - bodyH * 0.06 + bob - strike * 4 + (pulling ? 1.5 : 0);
    const element = !m.isBoss ? ELEMENT_OF[m.familyId] : undefined;
    if (element) this.drawElementAura(element, x, y, bodyH);
    if (m.isBoss) {
      ctx.save(); ctx.translate(x,drawY); ctx.scale(squash,stretch);
      drawBossMark(ctx,m.familyId === 'siege' ? 'siege_engine' : m.familyId,0,0,bodyH,facing,{time:m.age,walk:stride,attack:strike});
      ctx.restore();
    } else if (!m.isBoss) {
      ctx.save();
      if (m.slowTimer > 0) {
        ctx.shadowColor = '#7fd6ff';
        ctx.shadowBlur = 12;
      }
      ctx.translate(x, drawY);
      ctx.rotate(facing + stride * 0.04);
      ctx.scale(squash, stretch);
      drawMinionMark(ctx, m.familyId, 0, 0, bodyH, m.rank, { time: m.age, walk: stride, attack: Math.max(strike, pulling ? (0.45 - m.attackTimer) / 0.45 : 0) });
      ctx.restore();
    }
    if (element && frontMotes) this.drawElementMotes(element, x, y, bodyH, stepping, m.age, m.id);
    this.drawMinionAction(m, x, drawY, bodyH, facing);

    if (m.isBoss) {
      ctx.strokeStyle = rgba(fam?.color ?? '#ffd23a', m.isBoss ? 0.7 : 0.2 + 0.1 * m.rank);
      ctx.lineWidth = m.rank >= 5 || m.isBoss ? 3 : 2;
      ctx.beginPath();
      ctx.ellipse(x, y + bodyH * 0.42, bodyH * 0.32, bodyH * 0.12, 0, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Hit flash
    if (m.hitFlash > 0) {
      ctx.fillStyle = `rgba(255,255,255,${clamp(m.hitFlash * 5, 0, 0.7)})`;
      ctx.beginPath();
      ctx.arc(x, y, bodyH * 0.45, 0, Math.PI * 2);
      ctx.fill();
    }

    // Slow icon
    if (m.slowTimer > 0) {
      ctx.fillStyle = '#bfe5ff';
      ctx.font = `700 ${10 * s}px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('❄', x + bodyH * 0.5, y - bodyH * 0.4);
    }

    // Bosses keep a bar. Ordinary minions show the hit as floating damage instead.
    if (m.isBoss) {
      const w = 60;
      const h = 6;
      const bx = x - w / 2;
      const by = y - bodyH * 0.65 - 8;
      ctx.fillStyle = 'rgba(0,0,0,0.6)';
      ctx.fillRect(bx - 1, by - 1, w + 2, h + 2);
      const k = clamp(m.hp / m.maxHp, 0, 1);
      ctx.fillStyle = m.side === this.viewSide ? '#5dfc8a' : '#ff5c5c';
      ctx.fillRect(bx, by, w * k, h);
    }
    if (m.isBoss && m.bossName) {
      ctx.font = `800 10px ${FONT}`;
      ctx.fillStyle = '#ffd23a';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(0,0,0,0.7)';
      ctx.strokeText(m.bossName.toUpperCase(), x, y - bodyH * 0.65 - 18);
      ctx.fillText(m.bossName.toUpperCase(), x, y - bodyH * 0.65 - 18);
    }
    // Elite marker
    if (!m.isBoss && m.rank >= 4) {
      drawIcon(this.ctx, assets.ui.star, x - bodyH * 0.5, y - bodyH * 0.45, 9, '#ffd23a');
    }
    ctx.restore();
  }

  private drawProjectiles(match: Match): void {
    for (const p of match.projectiles) this.drawProjectile(p);
  }

  private drawProjectile(p: ProjectileState): void {
    const ctx = this.ctx;
    switch (p.kind) {
      case 'bolt':
      case 'gate': {
        const gate = p.kind === 'gate';
        ctx.save();
        ctx.translate(p.x, p.y);
        if (this.viewSide === 1) ctx.scale(1, -1);
        ctx.rotate(p.rotation);
        ctx.fillStyle = gate ? '#ffffff' : '#e9b8ff';
        ctx.shadowColor = gate ? SIDE_COLORS[p.side] : '#b13cff';
        ctx.shadowBlur = 8;
        this.roundRect(ctx, -6, -2, 12, 4, 2);
        ctx.fill();
        ctx.restore();
        break;
      }
      case 'frost': {
        ctx.save();
        ctx.translate(p.x, p.y);
        if (this.viewSide === 1) ctx.scale(1, -1);
        ctx.rotate(p.rotation + this.time * 6);
        ctx.fillStyle = '#bfe5ff';
        ctx.shadowColor = '#3fa9ff';
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.moveTo(0, -6);
        ctx.lineTo(5, 0);
        ctx.lineTo(0, 6);
        ctx.lineTo(-5, 0);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        break;
      }
      case 'fireball': {
        const img = assets.env.fireball;
        ctx.save();
        ctx.translate(p.x, p.y);
        if (this.viewSide === 1) ctx.scale(1, -1);
        ctx.rotate(p.rotation + Math.PI / 2);
        ctx.shadowColor = '#ff7a2a';
        ctx.shadowBlur = 12;
        if (img) ctx.drawImage(img, -11, -11, 22, 22);
        else {
          ctx.fillStyle = '#ff7a2a';
          ctx.beginPath();
          ctx.arc(0, 0, 7, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
        break;
      }
      case 'fire': {
        ctx.save();
        ctx.fillStyle = 'rgba(255,140,40,0.9)';
        ctx.shadowColor = '#ff5a1a';
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = 'rgba(255,230,120,0.9)';
        ctx.beginPath();
        ctx.arc(p.x, p.y, 2.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        break;
      }
      case 'arrow': {
        ctx.save();
        ctx.translate(p.x, p.y);
        if (this.viewSide === 1) ctx.scale(1, -1);
        ctx.rotate(p.rotation);
        ctx.strokeStyle = '#ffe9b0';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(-8, 0);
        ctx.lineTo(8, 0);
        ctx.stroke();
        ctx.fillStyle = '#ffe9b0';
        ctx.beginPath();
        ctx.moveTo(9, 0);
        ctx.lineTo(4, -3);
        ctx.lineTo(4, 3);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        break;
      }
      case 'mortar': {
        const k = clamp(p.t / p.duration, 0, 1);
        // Ground shadow / target marker
        ctx.strokeStyle = rgba('#ffd23a', 0.35 + 0.4 * k);
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.arc(p.tx, p.ty, p.splash * (0.4 + 0.6 * k), 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#2a2320';
        ctx.beginPath();
        ctx.arc(p.x, p.y, 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#ffd23a';
        ctx.beginPath();
        ctx.arc(p.x - 2, p.y - 2, 2, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      default:
        break;
    }
  }

  private drawLasers(match: Match): void {
    const ctx = this.ctx;
    let byId: Map<number, MinionState> | null = null;
    for (const side of [0, 1] as Side[]) {
      for (const t of match.players[side].towers) {
        if (!t) continue;
        const fam = FAMILY_BY_ID[t.familyId];
        if (fam.tower.attack !== 'laser' || t.targetId < 0 || t.sinceShot > 0.2) continue;
        if (!byId) {
          byId = new Map();
          for (const m of match.minions) byId.set(m.id, m);
        }
        const target = byId.get(t.targetId);
        if (!target) continue;
        const c = tileCenter(side, t.tile);
        const xy = minionXY(target);
        const k = 1 - t.sinceShot / 0.2;
        ctx.save();
        ctx.lineCap = 'round';
        ctx.strokeStyle = rgba('#63c93c', 0.35 * k);
        ctx.lineWidth = 7;
        ctx.beginPath();
        ctx.moveTo(c.x, c.y);
        ctx.lineTo(xy.x, xy.y);
        ctx.stroke();
        ctx.strokeStyle = rgba('#d3ffb8', 0.9 * k);
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.restore();
      }
    }
  }

  // ------------------------------------------------------------------- HUD

  private drawTopHud(match: Match): void {
    const ctx = this.ctx;
    const p1 = match.players[(1 - this.viewSide) as Side];
    const cmd = COMMANDER_BY_ID[p1.commanderId];

    // Enemy label
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.font = `800 11px ${FONT}`;
    ctx.fillStyle = SIDE_COLORS[1];
    ctx.fillText('ENEMY', 14, 16);
    ctx.font = `600 11px ${FONT}`;
    ctx.fillStyle = HUD_MUTED;
    const raw = match.config.opponent === 'human' ? 'online' : match.config.difficulty;
    const vs = raw.charAt(0).toUpperCase() + raw.slice(1);
    ctx.fillText(`${cmd.name} · ${vs}`, 14, 34);

    // Timer
    ctx.textAlign = 'center';
    ctx.font = `800 22px ${FONT}`;
    ctx.fillStyle = match.overtime ? '#e24b62' : HUD_INK;
    ctx.fillText(formatTime(match.time), LAYOUT.width / 2, 16);
    ctx.font = `700 11px ${FONT}`;
    if (match.overtime) {
      const blink = Math.sin(this.time * 6) > 0;
      ctx.fillStyle = blink ? '#ff5c5c' : '#ffb0b0';
      ctx.fillText(`Sudden death · leaks x${match.rules.overtimeLeakMultiplier}`, LAYOUT.width / 2, 40);
    } else {
      const untilBoss = match.nextBossIn();
      const untilDeath = match.rules.overtimeAt - match.time;
      ctx.fillStyle = HUD_MUTED;
      if (untilBoss > 0 && untilBoss < untilDeath) {
        ctx.fillText(`Wave ${match.waveNumber()} · bosses ${formatTime(untilBoss)}`, LAYOUT.width / 2, 40);
      } else ctx.fillText(`Sudden death in ${formatTime(untilDeath)}`, LAYOUT.width / 2, 40);
    }

    // Pause
    const r = HUD_RECTS.pause;
    ctx.fillStyle = 'rgba(42, 51, 68, 0.06)';
    this.roundRect(ctx, r.x, r.y, r.w, r.h, 10);
    ctx.fill();
    drawIcon(ctx, assets.ui.pause, r.x + r.w / 2, r.y + r.h / 2, 20, HUD_INK, 0.9);
  }

  private drawDeckRow(match: Match, hud: HudState): void {
    const ctx = this.ctx;
    const p = match.players[this.viewSide];
    for (const chip of deckChips()) {
      const held = hud.inspect?.kind === chip.kind && hud.inspect.index === chip.index;
      const r = chip.rect;
      ctx.fillStyle = held ? 'rgba(255, 193, 90, 0.45)' : 'rgba(255, 253, 248, 0.92)';
      this.roundRect(ctx, r.x, r.y, r.w, r.h, 10);
      ctx.fill();
      if (chip.kind === 'unit') {
        const id = p.deck[chip.index];
        const fam = id ? FAMILY_BY_ID[id] : null;
        if (!fam) continue;
        const element = ELEMENT_OF[fam.id];
        ctx.strokeStyle = element ? ELEMENT_STYLE[element].color : fam.color;
        ctx.lineWidth = held ? 3 : 1.5;
        this.roundRect(ctx, r.x, r.y, r.w, r.h, 10);
        ctx.stroke();
        drawTowerMark(ctx, fam.id, r.x + r.w / 2, r.y + r.h / 2 + 1, 40, 1);
      } else {
        const id = p.bosses[chip.index];
        const boss = id ? BOSS_BY_ID[id] : null;
        if (!boss) continue;
        ctx.strokeStyle = 'rgba(255,210,58,0.8)';
        ctx.lineWidth = held ? 3 : 1.5;
        this.roundRect(ctx, r.x, r.y, r.w, r.h, 10);
        ctx.stroke();
        drawBossMark(ctx,boss.id,r.x+r.w/2,r.y+r.h/2,36);
      }
    }
  }

  private drawInspect(match: Match, hud: HudState): void {
    if (!hud.inspect) return;
    const ctx = this.ctx;
    const p = match.players[this.viewSide];
    const card: Rect = { x: 18, y: 72, w: LAYOUT.width - 36, h: 268 };
    ctx.fillStyle = 'rgba(255, 253, 248, 0.96)';
    this.roundRect(ctx, card.x, card.y, card.w, card.h, 16);
    ctx.fill();
    ctx.strokeStyle = 'rgba(42, 51, 68, 0.12)';
    ctx.lineWidth = 2;
    this.roundRect(ctx, card.x, card.y, card.w, card.h, 16);
    ctx.stroke();
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    let y = card.y + 14;
    const x = card.x + 16;
    const wrap = (text: string, size: number, color: string, max: number) => {
      ctx.font = `600 ${size}px ${FONT}`;
      ctx.fillStyle = color;
      const words = text.split(' ');
      let line = '';
      for (const word of words) {
        const next = line ? `${line} ${word}` : word;
        if (ctx.measureText(next).width > max && line) {
          ctx.fillText(line, x, y);
          y += size + 4;
          line = word;
        } else line = next;
      }
      if (line) {
        ctx.fillText(line, x, y);
        y += size + 6;
      }
    };
    if (hud.inspect.kind === 'boss') {
      const boss = BOSS_BY_ID[p.bosses[hud.inspect.index]];
      if (!boss) return;
      ctx.font = `800 18px ${FONT}`;
      ctx.fillStyle = ESSENCE_INK;
      ctx.fillText(boss.name, x, y);
      y += 26;
      wrap(boss.description, 13, HUD_INK, card.w - 32);
      if (boss.aura) wrap(`Deck passive: ${boss.aura.label}. Active the whole match.`, 13, '#149e8e', card.w - 32);
      return;
    }
    const id = p.deck[hud.inspect.index];
    if (!id) return;
    const view = match.unitPreview(this.viewSide, id);
    const element = ELEMENT_OF[id];
    ctx.font = `800 18px ${FONT}`;
    ctx.fillStyle = HUD_INK;
    ctx.fillText(`${view.name} · ${view.role} · rank ${view.rank}`, x, y);
    y += 26;
    if (element) {
      ctx.font = `800 13px ${FONT}`;
      ctx.fillStyle = ELEMENT_STYLE[element].deep;
      ctx.fillText(ELEMENT_STYLE[element].name.toUpperCase(), x, y);
      y += 18;
      wrap(ELEMENT_STYLE[element].blurb, 12, HUD_MUTED, card.w - 32);
    }
    const n = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1));
    wrap(
      view.attack === 'support'
        ? `Does not shoot. Empowers neighbours. Musters every ${n(view.muster)}s.`
        : `Tower ${n(view.damage)} dmg · fires every ${n(view.fire)}s · range ${FAMILY_BY_ID[id].tower.range} · musters every ${n(view.muster)}s.`,
      13,
      HUD_INK,
      card.w - 32,
    );
    wrap(`${view.minionName}: ${view.minionHp} hp · ${n(view.minionDamage)} dmg · speed ${Math.round(view.minionSpeed)} · mass ${view.minionMass.toFixed(1)}.`, 13, HUD_INK, card.w - 32);
    wrap(view.about, 12, HUD_MUTED, card.w - 32);
    wrap(view.minionAbout, 12, HUD_MUTED, card.w - 32);
    if (view.notes.length) wrap(view.notes.join(' · '), 12, ESSENCE_INK, card.w - 32);
  }

  private drawBottomHud(match: Match, hud: HudState): void {
    const ctx = this.ctx;
    const p = match.players[this.viewSide];
    const y0 = LAYOUT.bottomHud.y;

    // Essence pill
    const ess = Math.floor(p.essence);
    ctx.fillStyle = 'rgba(255,210,58,0.12)';
    this.roundRect(ctx, 10, y0 + 6, 150, 24, 12);
    ctx.fill();
    drawIcon(ctx, assets.ui.spark, 26, y0 + 18, 16, ESSENCE_COLOR);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.font = `800 15px ${FONT}`;
    ctx.fillStyle = hud.lowEssenceFlash > 0 && Math.sin(this.time * 30) > 0 ? '#e24b62' : ESSENCE_INK;
    ctx.fillText(String(ess), 40, y0 + 18);

    const counts = match.laneCounts(this.viewSide);

    // Right pill: minions per lane
    ctx.fillStyle = 'rgba(42, 51, 68, 0.06)';
    this.roundRect(ctx, 380, y0 + 6, 150, 24, 12);
    ctx.fill();
    ctx.textAlign = 'left';
    ctx.font = `800 12px ${FONT}`;
    ctx.fillStyle = LANE_COLORS[0];
    ctx.fillText(`◄ ${counts[0]}`, 392, y0 + 18);
    ctx.textAlign = 'right';
    ctx.fillStyle = LANE_COLORS[1];
    ctx.fillText(`${counts[1]} ►`, 518, y0 + 18);
    ctx.textAlign = 'center';
    ctx.font = `600 9px ${FONT}`;
    ctx.fillStyle = HUD_MUTED;
    ctx.fillText('lanes', 455, y0 + 18);

    // Summon button
    const cost = match.summonCost(0);
    const canSummon = p.essence >= cost && match.emptyTiles(0).length > 0;
    this.drawButton(HUD_RECTS.summon, canSummon ? '#ff8a3d' : '#e7e0d4', hud.pressed === 'summon', hud.highlight === 'summon');
    {
      const r = HUD_RECTS.summon;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `800 20px ${FONT}`;
      ctx.fillStyle = canSummon ? '#fffdf8' : '#8a8174';
      ctx.fillText('SUMMON', r.x + r.w / 2, r.y + 26);
      if (p.wildcardArmed) {
        ctx.font = `800 13px ${FONT}`;
        ctx.fillStyle = '#4ad9c8';
        ctx.fillText('WILDCARD · FREE', r.x + r.w / 2, r.y + 54);
      } else if (match.emptyTiles(0).length === 0) {
        ctx.font = `700 12px ${FONT}`;
        ctx.fillStyle = '#ffb0b0';
        ctx.fillText('BOARD FULL', r.x + r.w / 2, r.y + 54);
      } else {
        drawIcon(ctx, assets.ui.spark, r.x + r.w / 2 - 22, r.y + 54, 14, canSummon ? ESSENCE_INK : '#8a8174');
        ctx.font = `800 15px ${FONT}`;
        ctx.fillStyle = canSummon ? '#3a2410' : '#8a8174';
        ctx.fillText(String(cost), r.x + r.w / 2 + 8, r.y + 54);
      }
    }

    // Shop buttons
    SHOP.forEach((s, i) => {
      const r = HUD_RECTS.shop[i];
      const lvl = p.shop[s.id];
      const c = match.shopCost(this.viewSide, s.id);
      const affordable = c !== null && p.essence >= c;
      this.drawButton(r, affordable ? s.color : '#efe8dc', hud.pressed === `shop:${s.id}`, hud.highlight === 'shop', 8, affordable ? 0.85 : 1);
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.font = `800 11px ${FONT}`;
      ctx.fillStyle = affordable ? '#1a1400' : '#6d6578';
      ctx.fillText(s.shortName, r.x + 8, r.y + 12);
      ctx.font = `700 9px ${FONT}`;
      ctx.fillStyle = affordable ? 'rgba(0,0,0,0.65)' : '#8a8494';
      ctx.fillText(`LV ${lvl}/${SHOP_MAX_LEVEL}`, r.x + 8, r.y + 26);
      ctx.textAlign = 'right';
      ctx.font = `800 12px ${FONT}`;
      if (c === null) {
        ctx.fillStyle = '#8a8494';
        ctx.fillText('MAX', r.x + r.w - 8, r.y + 18);
      } else {
        ctx.fillStyle = affordable ? '#1a1400' : '#8a8494';
        ctx.fillText(String(c), r.x + r.w - 8, r.y + 18);
      }
      // Level pips
      for (let k = 0; k < SHOP_MAX_LEVEL; k++) {
        ctx.fillStyle = k < lvl ? (affordable ? '#1a1400' : s.color) : 'rgba(42, 51, 68, 0.15)';
        ctx.fillRect(r.x + r.w - 8 - (SHOP_MAX_LEVEL - k) * 7, r.y + r.h - 8, 5, 3);
      }
    });

    // Commander button
    const cmd = COMMANDER_BY_ID[p.commanderId];
    const ready = p.commanderCooldown <= 0 && !(p.commanderId === 'oda' && p.wildcardArmed);
    this.drawButton(HUD_RECTS.commander, ready ? cmd.color : '#e7e0d4', hud.pressed === 'commander', hud.highlight === 'commander');
    {
      const r = HUD_RECTS.commander;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `700 10px ${FONT}`;
      ctx.fillStyle = ready ? 'rgba(0,0,0,0.7)' : '#8a8174';
      ctx.fillText(cmd.name.toUpperCase(), r.x + r.w / 2, r.y + 16);
      ctx.font = `800 18px ${FONT}`;
      ctx.fillStyle = ready ? '#ffffff' : '#8a8174';
      ctx.fillText(cmd.activeName.toUpperCase(), r.x + r.w / 2, r.y + 38);
      ctx.font = `700 11px ${FONT}`;
      if (p.commanderActive > 0) {
        ctx.fillStyle = '#ffffff';
        ctx.fillText(`ACTIVE ${p.commanderActive.toFixed(1)}s`, r.x + r.w / 2, r.y + 60);
      } else if (p.wildcardArmed) {
        ctx.fillStyle = '#ffffff';
        ctx.fillText('ARMED', r.x + r.w / 2, r.y + 60);
      } else if (!ready) {
        ctx.fillStyle = '#6d6578';
        ctx.fillText(`${Math.ceil(p.commanderCooldown)}s`, r.x + r.w / 2, r.y + 60);
        // Cooldown sweep
        const k = 1 - p.commanderCooldown / cmd.cooldown;
        ctx.fillStyle = rgba(cmd.color, 0.7);
        ctx.fillRect(r.x + 8, r.y + r.h - 7, (r.w - 16) * k, 3);
      } else {
        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        ctx.fillText('Ready', r.x + r.w / 2, r.y + 60);
      }
    }
  }

  private drawButton(r: Rect, color: string, pressed: boolean, highlight: boolean, radius = 12, alpha = 1): void {
    const ctx = this.ctx;
    ctx.save();
    const off = pressed ? 2 : 0;
    ctx.fillStyle = 'rgba(90, 60, 20, 0.16)';
    this.roundRect(ctx, r.x, r.y + 3, r.w, r.h, radius);
    ctx.fill();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = color;
    this.roundRect(ctx, r.x, r.y + off, r.w, r.h - off, radius);
    ctx.fill();
    ctx.globalAlpha = 1;
    const grad = ctx.createLinearGradient(0, r.y, 0, r.y + r.h);
    grad.addColorStop(0, 'rgba(255,255,255,0.18)');
    grad.addColorStop(1, 'rgba(0,0,0,0.12)');
    ctx.fillStyle = grad;
    this.roundRect(ctx, r.x, r.y + off, r.w, r.h - off, radius);
    ctx.fill();
    if (highlight) {
      const pulse = 0.5 + 0.5 * Math.sin(this.time * 6);
      ctx.strokeStyle = rgba('#ffffff', 0.5 + 0.5 * pulse);
      ctx.lineWidth = 3;
      this.roundRect(ctx, r.x - 3, r.y - 3, r.w + 6, r.h + 6, radius + 3);
      ctx.stroke();
    }
    ctx.restore();
  }

  private tileScreen(tile: number): { x: number; y: number } {
    const c = tileCenter(this.viewSide, tile);
    return this.viewSide === 1 ? { x: c.x, y: LAYOUT.height - c.y } : c;
  }

  private drawCoach(coach: Coach): void {
    const ctx = this.ctx;
    if (coach.drag) {
      const a = this.tileScreen(coach.drag.from);
      const b = this.tileScreen(coach.drag.to);
      const phase = (this.time % 1.7) / 1.7;
      const e = phase < 0.12 ? 0 : phase > 0.82 ? 1 : (phase - 0.12) / 0.7;
      const ease = e * e * (3 - 2 * e);
      const x = a.x + (b.x - a.x) * ease;
      const y = a.y + (b.y - a.y) * ease;
      ctx.save();
      ctx.strokeStyle = 'rgba(255, 138, 61, 0.95)';
      ctx.lineWidth = 3;
      ctx.setLineDash([7, 7]);
      ctx.lineDashOffset = -this.time * 28;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      ctx.setLineDash([]);
      const sx = Math.sign(b.x - a.x) || 0;
      const sy = Math.sign(b.y - a.y) || 0;
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#ff8a3d';
      ctx.beginPath();
      ctx.moveTo(b.x - sx * 16 - sy * 8, b.y - sy * 16 + sx * 8);
      ctx.lineTo(b.x - sx * 4, b.y - sy * 4);
      ctx.lineTo(b.x - sx * 16 + sy * 8, b.y - sy * 16 - sx * 8);
      ctx.stroke();
      ctx.fillStyle = 'rgba(90, 60, 20, 0.18)';
      ctx.beginPath();
      ctx.arc(x, y + 3, 16, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fffdf8';
      ctx.beginPath();
      ctx.arc(x, y, 15, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#ff8a3d';
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.restore();
    }
    if (!coach.arrow) return;
    let x = 0;
    let y = 0;
    if (coach.arrow.kind === 'summon') {
      const r = HUD_RECTS.summon;
      x = r.x + r.w / 2;
      y = r.y - 2;
    } else if (coach.arrow.kind === 'shop') {
      const r = HUD_RECTS.shop[0];
      x = r.x + r.w / 2;
      y = r.y - 2;
    } else if (coach.arrow.kind === 'bosses') {
      const chip = deckChips().find((c) => c.kind === 'boss');
      if (!chip) return;
      x = chip.rect.x + chip.rect.w / 2;
      y = chip.rect.y - 2;
    } else if (coach.arrow.kind === 'tile') {
      const c = this.tileScreen(coach.arrow.tile);
      x = c.x;
      y = c.y - 40;
    } else if (coach.arrow.kind === 'bond') {
      const a = this.tileScreen(coach.arrow.a);
      const b = this.tileScreen(coach.arrow.b);
      x = (a.x + b.x) / 2 - 48;
      y = (a.y + b.y) / 2;
      this.drawPointer(x, y, 'right');
      return;
    } else return;
    this.drawPointer(x, y, 'down');
  }

  private drawPointer(x: number, y: number, dir: 'down' | 'right'): void {
    const ctx = this.ctx;
    const bob = Math.sin(this.time * 5) * 5;
    const tipX = dir === 'right' ? x + bob : x;
    const tipY = dir === 'down' ? y + bob : y;
    const tailX = dir === 'right' ? tipX - 30 : tipX;
    const tailY = dir === 'down' ? tipY - 30 : tipY;
    const neckX = dir === 'right' ? tipX - 10 : tipX;
    const neckY = dir === 'down' ? tipY - 10 : tipY;
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const head = (color: string, width: number, size: number): void => {
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(tailX, tailY);
      ctx.lineTo(neckX, neckY);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(tipX, tipY);
      if (dir === 'down') {
        ctx.lineTo(tipX - size, tipY - size);
        ctx.lineTo(tipX + size, tipY - size);
      } else {
        ctx.lineTo(tipX - size, tipY - size);
        ctx.lineTo(tipX - size, tipY + size);
      }
      ctx.closePath();
      ctx.fill();
    };
    head('#fffdf8', 7, 10);
    head('#ff8a3d', 4, 8);
    ctx.restore();
  }

  private drawOverlays(match: Match, hud: HudState): void {
    const ctx = this.ctx;
    const midY = FIELD_TOP + LAYOUT.field.h / 2;

    if (hud.banner) {
      const b = hud.banner;
      const k = b.life / b.maxLife;
      const enter = clamp((b.maxLife - b.life) / 0.3, 0, 1);
      const alpha = Math.min(1, k * 3) * enter;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = 'rgba(255, 248, 236, 0.94)';
      ctx.fillRect(0, midY - 40, LAYOUT.width, 80);
      ctx.fillStyle = b.color;
      ctx.fillRect(0, midY - 40, LAYOUT.width, 3);
      ctx.fillRect(0, midY + 37, LAYOUT.width, 3);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `800 22px ${FONT}`;
      ctx.fillStyle = HUD_INK;
      ctx.fillText(b.title, LAYOUT.width / 2, midY - 16);
      ctx.font = `600 13px ${FONT}`;
      ctx.fillStyle = HUD_MUTED;
      const lines = this.wrap(ctx, b.subtitle, LAYOUT.width - 56).slice(0, 2);
      lines.forEach((line, i) => ctx.fillText(line, LAYOUT.width / 2, midY + 8 + i * 16));
      ctx.restore();
    }

    if (hud.toast) {
      const t = hud.toast;
      const k = t.life / t.maxLife;
      ctx.save();
      ctx.globalAlpha = Math.min(1, k * 3);
      ctx.font = `700 13px ${FONT}`;
      const w = ctx.measureText(t.text).width + 32;
      const y = hud.banner ? midY + 70 : midY + 40;
      ctx.fillStyle = 'rgba(255, 253, 248, 0.94)';
      this.roundRect(ctx, LAYOUT.width / 2 - w / 2, y - 16, w, 32, 16);
      ctx.fill();
      ctx.strokeStyle = rgba(t.color, 0.8);
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = HUD_INK;
      ctx.fillText(t.text, LAYOUT.width / 2, y);
      ctx.restore();
    }

    if (hud.tutorial) {
      const r = TUTORIAL_RECT;
      ctx.save();
      ctx.fillStyle = 'rgba(255, 253, 248, 0.96)';
      this.roundRect(ctx, r.x, r.y, r.w, r.h, 10);
      ctx.fill();
      ctx.strokeStyle = 'rgba(42, 51, 68, 0.12)';
      ctx.lineWidth = 1.5;
      this.roundRect(ctx, r.x, r.y, r.w, r.h, 10);
      ctx.stroke();
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.font = `800 10px ${FONT}`;
      ctx.fillStyle = ESSENCE_INK;
      ctx.fillText(`TUTORIAL ${hud.tutorial.step}/${hud.tutorial.total}`, r.x + 12, r.y + 13);
      ctx.font = `600 12px ${FONT}`;
      ctx.fillStyle = HUD_INK;
      const lines = this.wrap(ctx, hud.tutorial.text, r.w - 36);
      lines.slice(0, 3).forEach((line, i) => ctx.fillText(line, r.x + 12, r.y + 36 + i * 16));
      const s = TUTORIAL_SKIP_RECT;
      ctx.fillStyle = 'rgba(42, 51, 68, 0.06)';
      this.roundRect(ctx, s.x, s.y, s.w, s.h, 11);
      ctx.fill();
      ctx.textAlign = 'center';
      ctx.font = `700 10px ${FONT}`;
      ctx.fillStyle = HUD_INK;
      ctx.fillText('SKIP', s.x + s.w / 2, s.y + s.h / 2);
      ctx.restore();
    }

    if (hud.coach) this.drawCoach(hud.coach);

    if (hud.info) {
      const t = match.players[this.viewSide].towers[hud.info.tile];
      if (t) {
        const fam = FAMILY_BY_ID[t.familyId];
        const k = clamp(hud.info.life / 0.3, 0, 1);
        const y = LAYOUT.playerGrid.y - 70;
        ctx.save();
        ctx.globalAlpha = k;
        ctx.fillStyle = 'rgba(255, 253, 248, 0.96)';
        this.roundRect(ctx, 16, y, LAYOUT.width - 32, 62, 10);
        ctx.fill();
        ctx.strokeStyle = rgba(fam.color, 0.9);
        ctx.lineWidth = 2;
        this.roundRect(ctx, 16, y, LAYOUT.width - 32, 62, 10);
        ctx.stroke();
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.font = `800 14px ${FONT}`;
        ctx.fillStyle = HUD_INK;
        ctx.fillText(`${fam.name.toUpperCase()}  ·  RANK ${t.rank}`, 30, y + 16);
        ctx.textAlign = 'right';
        ctx.fillStyle = LANE_COLORS[t.lane];
        ctx.fillText(`${t.lane === 0 ? '◄ ' : ''}${LANE_NAMES[t.lane]} LANE${t.lane === 1 ? ' ►' : ''}`, LAYOUT.width - 30, y + 16);
        ctx.textAlign = 'left';
        ctx.font = `600 11px ${FONT}`;
        ctx.fillStyle = HUD_INK;
        const interval = match.spawnInterval(this.viewSide, fam, t.rank, t.supportBonus);
        const line1 = fam.tower.attack === 'support' ? `${fam.role}: buffs adjacent towers +${Math.round((fam.tower.support ?? 0) * t.rank * 100)}% damage.` : `${fam.role}: ${fam.description.split('.')[0]}.`;
        ctx.fillText(this.ellipsize(ctx, line1, LAYOUT.width - 60), 30, y + 36);
        ctx.fillStyle = HUD_MUTED;
        const hint = match.board === 'painted' ? 'drag to another color · 5 Essence' : 'tap to switch lane';
        ctx.fillText(this.ellipsize(ctx, `Musters ${fam.minion.name} every ${interval.toFixed(1)}s · ${hint} · merge twins`, LAYOUT.width - 60), 30, y + 51);
        ctx.restore();
      }
    }
  }

  private wrap(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let cur = '';
    for (const w of words) {
      const test = cur ? `${cur} ${w}` : w;
      if (ctx.measureText(test).width > maxW && cur) {
        lines.push(cur);
        cur = w;
      } else cur = test;
    }
    if (cur) lines.push(cur);
    return lines;
  }

  private ellipsize(ctx: CanvasRenderingContext2D, text: string, maxW: number): string {
    if (ctx.measureText(text).width <= maxW) return text;
    let t = text;
    while (t.length > 3 && ctx.measureText(t + '…').width > maxW) t = t.slice(0, -1);
    return t + '…';
  }

  /** Which lane an x coordinate belongs to, for hit-testing overlays. */
  static laneAt(x: number): Lane | null {
    for (let l = 0; l < 2; l++) if (Math.abs(x - LAYOUT.lanes.x[l]) < LAYOUT.lanes.width / 2 + 8) return l as Lane;
    return null;
  }

  static get laneLength(): number {
    return LANE_LENGTH;
  }
}
